import React, { useMemo, useState } from 'react';
import {
  AlertTriangle,
  Building2,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleDollarSign,
  Clock,
  Edit,
  FileText,
  History,
  Landmark,
  Laptop,
  Megaphone,
  Package,
  Plus,
  Search,
  Stethoscope,
  Trash2,
  Wallet,
  X,
  Zap,
} from 'lucide-react';
import axios from 'axios';
import CurrencyInput from '../common/CurrencyInput';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const emptyExpense = {
  description: '',
  category_id: '',
  supplier_id: '',
  supplier_name: '',
  issue_date: '',
  due_date: '',
  competence: '',
  total_amount: '',
  paid_amount: '',
  payment_method_id: '',
  payment_method_name: '',
  document_number: '',
  reference_document: '',
  notes: '',
  payment_type: 'single',
  installment_count: 2,
  first_installment_date: '',
};

const statusMap = {
  pending: ['Pendente', 'bg-amber-100 text-amber-800 ring-amber-200'],
  partially_paid: ['Parcialmente paga', 'bg-blue-100 text-blue-800 ring-blue-200'],
  paid: ['Paga', 'bg-green-100 text-green-800 ring-green-200'],
  overdue: ['Vencida', 'bg-red-100 text-red-800 ring-red-200'],
  cancelled: ['Cancelada', 'bg-gray-100 text-gray-700 ring-gray-200'],
};

const categoryIconRules = [
  { terms: ['energia', 'elétrica', 'eletrica', 'luz'], icon: Zap },
  { terms: ['laboratório', 'laboratorio', 'clínico', 'clinico'], icon: Stethoscope },
  { terms: ['marketing', 'publicidade', 'anúncio', 'anuncio'], icon: Megaphone },
  { terms: ['imposto', 'tributo', 'taxa', 'fiscal'], icon: Landmark },
  { terms: ['software', 'sistema', 'tecnologia', 'internet'], icon: Laptop },
  { terms: ['equipamento', 'material', 'estoque', 'insumo'], icon: Package },
  { terms: ['aluguel', 'condomínio', 'condominio', 'estrutura'], icon: Building2 },
];

const toDate = (value) => value ? new Date(value).toLocaleDateString('pt-BR') : '—';

const dateOnly = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

const getDaysDifference = (value) => {
  const target = dateOnly(value);
  if (!target) return null;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((target.getTime() - today.getTime()) / 86400000);
};

const getDueBadge = (item) => {
  if (!item?.due_date || ['paid', 'cancelled'].includes(item.status)) return null;
  const days = getDaysDifference(item.due_date);
  if (days === null) return null;
  if (days < 0) return { label: `${Math.abs(days)} dia${Math.abs(days) === 1 ? '' : 's'} em atraso`, className: 'bg-red-50 text-red-700 ring-red-200' };
  if (days === 0) return { label: 'Vence hoje', className: 'bg-red-50 text-red-700 ring-red-200' };
  if (days === 1) return { label: 'Vence amanhã', className: 'bg-orange-50 text-orange-700 ring-orange-200' };
  if (days <= 7) return { label: `Vence em ${days} dias`, className: 'bg-amber-50 text-amber-700 ring-amber-200' };
  return null;
};

const getCategoryIcon = (categoryName) => {
  const normalized = String(categoryName || '').toLowerCase();
  return categoryIconRules.find((rule) => rule.terms.some((term) => normalized.includes(term)))?.icon || CircleDollarSign;
};

const ExpenseManagementPanel = ({
  expenses = [],
  categories = [],
  suppliers = [],
  paymentMethods = [],
  formatCurrency,
  onReload,
  toast,
}) => {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyExpense);
  const [saving, setSaving] = useState(false);
  const [paying, setPaying] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethodId, setPaymentMethodId] = useState('');
  const [expanded, setExpanded] = useState({});
  const [visibleCount, setVisibleCount] = useState(15);
  const [historyExpense, setHistoryExpense] = useState(null);
  const [historyPayments, setHistoryPayments] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const filtered = useMemo(() => expenses.filter((item) => {
    const haystack = `${item.description || ''} ${item.category_name || ''} ${item.supplier_name || ''} ${item.document_number || ''}`.toLowerCase();
    return (
      (!search || haystack.includes(search.toLowerCase())) &&
      (!status || item.status === status) &&
      (!categoryFilter || item.category_id === categoryFilter) &&
      (!supplierFilter || item.supplier_id === supplierFilter)
    );
  }), [expenses, search, status, categoryFilter, supplierFilter]);

  const totals = useMemo(() => filtered.reduce((acc, item) => {
    if (item.status === 'cancelled') return acc;
    const total = Number(item.total_amount || 0);
    const paid = Number(item.paid_amount || 0);
    const pending = Math.max(total - paid, 0);
    acc.total += total;
    acc.paid += paid;
    acc.pending += pending;
    acc.openCount += pending > 0 ? 1 : 0;
    if (item.status === 'overdue') acc.overdue += pending;
    const days = getDaysDifference(item.due_date);
    if (days !== null && days >= 0 && days <= 7 && pending > 0) acc.nextSevenDays += pending;
    return acc;
  }, { total: 0, paid: 0, pending: 0, overdue: 0, nextSevenDays: 0, openCount: 0 }), [filtered]);

  const averageExpense = useMemo(() => {
    const valid = filtered.filter((item) => item.status !== 'cancelled');
    return valid.length ? totals.total / valid.length : 0;
  }, [filtered, totals.total]);

  const visibleExpenses = filtered.slice(0, visibleCount);

  const openNew = () => {
    const today = new Date().toISOString().slice(0, 10);
    setEditing(null);
    setForm({
      ...emptyExpense,
      issue_date: today,
      due_date: today,
      first_installment_date: today,
      competence: today.slice(0, 7),
    });
    setShowForm(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      ...emptyExpense,
      ...item,
      issue_date: item.issue_date?.slice(0, 10) || '',
      due_date: item.due_date?.slice(0, 10) || '',
      first_installment_date: item.first_installment_date?.slice(0, 10) || item.due_date?.slice(0, 10) || '',
      competence: item.competence || '',
      total_amount: item.total_amount ?? '',
      paid_amount: item.paid_amount ?? '',
    });
    setShowForm(true);
  };

  const save = async (event) => {
    event.preventDefault();
    const dueDate = form.payment_type === 'installment' ? form.first_installment_date : form.due_date;
    if (!form.description || !form.category_id || !dueDate || !Number(form.total_amount)) {
      toast?.error('Preencha descrição, categoria, vencimento e valor.');
      return;
    }
    if (form.payment_type === 'installment' && Number(form.installment_count) < 2) {
      toast?.error('Informe pelo menos duas parcelas.');
      return;
    }

    try {
      setSaving(true);
      const category = categories.find((item) => item.id === form.category_id);
      const supplier = suppliers.find((item) => item.id === form.supplier_id);
      const payload = {
        ...form,
        supplier_name: supplier?.name || form.supplier_name || null,
        issue_date: form.issue_date ? `${form.issue_date}T12:00:00` : null,
        due_date: `${dueDate}T12:00:00`,
        first_installment_date: form.payment_type === 'installment' ? `${form.first_installment_date}T12:00:00` : null,
        installment_count: form.payment_type === 'installment' ? Number(form.installment_count || 2) : 1,
        total_amount: Number(form.total_amount || 0),
        paid_amount: form.payment_type === 'installment' ? 0 : Number(form.paid_amount || 0),
        category_name: category?.name,
      };

      if (editing) {
        const editPayload = { ...payload };
        delete editPayload.payment_type;
        delete editPayload.installment_count;
        delete editPayload.first_installment_date;
        if (editing.payment_type === 'installment') {
          delete editPayload.total_amount;
          delete editPayload.paid_amount;
          delete editPayload.due_date;
        }
        await axios.put(`${API_URL}/billing/expenses/${editing.id}`, editPayload, { headers: headers() });
        toast?.success('Despesa atualizada com sucesso.');
      } else {
        await axios.post(`${API_URL}/billing/expenses`, payload, { headers: headers() });
        toast?.success(form.payment_type === 'installment' ? 'Despesa parcelada cadastrada com sucesso.' : 'Despesa cadastrada com sucesso.');
      }

      setShowForm(false);
      setEditing(null);
      await onReload();
    } catch (error) {
      toast?.error(error.response?.data?.detail || 'Não foi possível salvar a despesa.');
    } finally {
      setSaving(false);
    }
  };

  const cancelExpense = async (item) => {
    if (!window.confirm(`Cancelar a despesa "${item.description}"?`)) return;
    try {
      await axios.delete(`${API_URL}/billing/expenses/${item.id}`, { headers: headers() });
      toast?.success('Despesa cancelada.');
      await onReload();
    } catch (error) {
      toast?.error(error.response?.data?.detail || 'Não foi possível cancelar a despesa.');
    }
  };

  const openPayment = (expense, installment = null) => {
    const total = Number(installment?.amount ?? expense.total_amount ?? 0);
    const paid = Number(installment?.paid_amount ?? expense.paid_amount ?? 0);
    setPaying({ expense, installment });
    setPaymentAmount(String(Math.max(total - paid, 0)));
    setPaymentMethodId('');
  };

  const pay = async () => {
    if (!paying || Number(paymentAmount || 0) <= 0) {
      toast?.error('Informe um valor válido.');
      return;
    }
    const method = paymentMethods.find((item) => item.id === paymentMethodId);
    const { expense, installment } = paying;
    const endpoint = installment
      ? `${API_URL}/billing/expenses/${expense.id}/installments/${installment.id}/payments`
      : `${API_URL}/billing/expenses/${expense.id}/payments`;

    try {
      await axios.post(endpoint, {
        amount: Number(paymentAmount || 0),
        payment_method_id: paymentMethodId || null,
        payment_method_name: method?.name || null,
      }, { headers: headers() });
      toast?.success(installment ? 'Pagamento da parcela registrado.' : 'Pagamento registrado com sucesso.');
      setPaying(null);
      setPaymentAmount('');
      setPaymentMethodId('');
      await onReload();
    } catch (error) {
      toast?.error(error.response?.data?.detail || 'Não foi possível registrar o pagamento.');
    }
  };

  const openHistory = async (expense) => {
    setHistoryExpense(expense);
    setHistoryPayments([]);
    setHistoryLoading(true);
    try {
      const response = await axios.get(`${API_URL}/billing/expenses/${expense.id}/payments`, { headers: headers() });
      setHistoryPayments(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast?.error(error.response?.data?.detail || 'Não foi possível carregar o histórico.');
      setHistoryExpense(null);
    } finally {
      setHistoryLoading(false);
    }
  };

  const installmentPreview = useMemo(() => {
    const total = Math.round(Number(form.total_amount || 0) * 100);
    const count = Math.max(Number(form.installment_count || 1), 1);
    if (!total || form.payment_type !== 'installment') return null;
    const base = Math.floor(total / count);
    const remainder = total % count;
    const first = (base + (remainder > 0 ? 1 : 0)) / 100;
    const last = base / 100;
    return { first, last, count };
  }, [form.total_amount, form.installment_count, form.payment_type]);

  const clearFilters = () => {
    setSearch('');
    setStatus('');
    setCategoryFilter('');
    setSupplierFilter('');
    setVisibleCount(15);
  };

  const summaryCards = [
    ['Despesas do período', totals.total, Wallet, 'text-gray-900', 'Soma dos lançamentos filtrados'],
    ['Total pago', totals.paid, CheckCircle2, 'text-green-700', 'Valores já baixados'],
    ['A vencer em 7 dias', totals.nextSevenDays, CalendarDays, 'text-orange-700', 'Compromissos mais próximos'],
    ['Vencido', totals.overdue, AlertTriangle, 'text-red-700', 'Saldo em atraso'],
    ['Valor médio', averageExpense, CircleDollarSign, 'text-purple-700', 'Média por lançamento'],
    ['Contas abertas', totals.openCount, FileText, 'text-blue-700', 'Lançamentos com saldo'],
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Despesas e contas a pagar</h2>
          <p className="mt-1 text-sm text-gray-600">Acompanhe vencimentos, parcelas, pagamentos e compromissos da clínica.</p>
        </div>
        <button onClick={openNew} className="inline-flex items-center justify-center rounded-lg bg-red-600 px-4 py-2.5 font-semibold text-white hover:bg-red-700">
          <Plus className="mr-2 h-4 w-4" /> Nova despesa
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {summaryCards.map(([label, value, Icon, valueClass, helper]) => (
          <div key={label} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-gray-600">{label}</p>
                <p className={`mt-2 text-2xl font-bold ${valueClass}`}>{label === 'Contas abertas' ? value : formatCurrency(value)}</p>
                <p className="mt-1 text-xs text-gray-500">{helper}</p>
              </div>
              <div className="rounded-xl bg-gray-50 p-2.5"><Icon className="h-5 w-5 text-gray-500" /></div>
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-100 p-4">
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-12">
            <div className="relative lg:col-span-5">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <input
                value={search}
                onChange={(e) => { setSearch(e.target.value); setVisibleCount(15); }}
                placeholder="Buscar descrição, categoria, fornecedor ou documento"
                className="w-full rounded-lg border border-gray-300 py-2.5 pl-9 pr-3 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-200"
              />
            </div>
            <select value={status} onChange={(e) => { setStatus(e.target.value); setVisibleCount(15); }} className="rounded-lg border border-gray-300 px-3 py-2.5 lg:col-span-2">
              <option value="">Todos os status</option>
              {Object.entries(statusMap).map(([value, [label]]) => <option key={value} value={value}>{label}</option>)}
            </select>
            <select value={categoryFilter} onChange={(e) => { setCategoryFilter(e.target.value); setVisibleCount(15); }} className="rounded-lg border border-gray-300 px-3 py-2.5 lg:col-span-2">
              <option value="">Todas as categorias</option>
              {categories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
            </select>
            <select value={supplierFilter} onChange={(e) => { setSupplierFilter(e.target.value); setVisibleCount(15); }} className="rounded-lg border border-gray-300 px-3 py-2.5 lg:col-span-2">
              <option value="">Todos os fornecedores</option>
              {suppliers.map((supplier) => <option key={supplier.id} value={supplier.id}>{supplier.name}</option>)}
            </select>
            <button type="button" onClick={clearFilters} className="rounded-lg border border-gray-300 px-3 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 lg:col-span-1">Limpar</button>
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-sm text-gray-500">
            <span>{filtered.length} despesa{filtered.length === 1 ? '' : 's'} encontrada{filtered.length === 1 ? '' : 's'}</span>
            <span>Saldo filtrado: <strong className="text-gray-800">{formatCurrency(totals.pending)}</strong></span>
          </div>
        </div>

        <div className="divide-y divide-gray-100">
          {visibleExpenses.map((item) => {
            const [statusLabel, statusClass] = statusMap[item.status] || [item.status, 'bg-gray-100 text-gray-700 ring-gray-200'];
            const balance = Math.max(Number(item.total_amount || 0) - Number(item.paid_amount || 0), 0);
            const progress = Number(item.total_amount || 0) > 0 ? Math.min((Number(item.paid_amount || 0) / Number(item.total_amount || 0)) * 100, 100) : 0;
            const installments = Array.isArray(item.installments) ? item.installments : [];
            const isExpanded = expanded[item.id];
            const dueBadge = getDueBadge(item);
            const CategoryIcon = getCategoryIcon(item.category_name);

            return (
              <div key={item.id} className="p-4 sm:p-5">
                <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start gap-3">
                      <div className="rounded-xl bg-purple-50 p-2.5 text-purple-700"><CategoryIcon className="h-5 w-5" /></div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="truncate text-base font-semibold text-gray-900">{item.description}</h3>
                          <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${statusClass}`}>{statusLabel}</span>
                          {dueBadge && <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${dueBadge.className}`}>{dueBadge.label}</span>}
                          {item.payment_type === 'installment' && <span className="rounded-full bg-purple-50 px-2.5 py-1 text-xs font-semibold text-purple-700 ring-1 ring-purple-200">{installments.length || item.installment_count} parcelas</span>}
                        </div>
                        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500">
                          <span>{item.category_name || 'Sem categoria'}</span>
                          {item.supplier_name && <span>Fornecedor: {item.supplier_name}</span>}
                          <span>Vencimento: {toDate(item.due_date)}</span>
                          {item.document_number && <span>Documento: {item.document_number}</span>}
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                      <div><p className="text-xs uppercase tracking-wide text-gray-500">Total</p><p className="mt-1 font-semibold text-gray-900">{formatCurrency(item.total_amount)}</p></div>
                      <div><p className="text-xs uppercase tracking-wide text-gray-500">Pago</p><p className="mt-1 font-semibold text-green-700">{formatCurrency(item.paid_amount)}</p></div>
                      <div><p className="text-xs uppercase tracking-wide text-gray-500">Saldo</p><p className="mt-1 font-semibold text-red-700">{formatCurrency(balance)}</p></div>
                      <div><p className="text-xs uppercase tracking-wide text-gray-500">Progresso</p><p className="mt-1 font-semibold text-gray-900">{Math.round(progress)}%</p></div>
                    </div>
                    <div className="mt-3 h-2 overflow-hidden rounded-full bg-gray-100">
                      <div className="h-full rounded-full bg-green-500 transition-all" style={{ width: `${progress}%` }} />
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 xl:max-w-xs xl:justify-end">
                    {item.payment_type === 'installment' && installments.length > 0 && (
                      <button onClick={() => setExpanded((current) => ({ ...current, [item.id]: !current[item.id] }))} className="inline-flex items-center rounded-lg border border-purple-200 px-3 py-2 text-sm font-semibold text-purple-700 hover:bg-purple-50">
                        {isExpanded ? <ChevronDown className="mr-1.5 h-4 w-4" /> : <ChevronRight className="mr-1.5 h-4 w-4" />}
                        Parcelas
                      </button>
                    )}
                    <button onClick={() => openHistory(item)} className="inline-flex items-center rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"><History className="mr-1.5 h-4 w-4" /> Histórico</button>
                    {balance > 0 && item.status !== 'cancelled' && item.payment_type !== 'installment' && (
                      <button onClick={() => openPayment(item)} className="inline-flex items-center rounded-lg bg-green-600 px-3 py-2 text-sm font-semibold text-white hover:bg-green-700"><Wallet className="mr-1.5 h-4 w-4" /> Pagar</button>
                    )}
                    <button onClick={() => openEdit(item)} className="inline-flex items-center rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"><Edit className="mr-1.5 h-4 w-4" /> Editar</button>
                    {item.status !== 'cancelled' && <button onClick={() => cancelExpense(item)} className="inline-flex items-center rounded-lg border border-red-200 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-50"><Trash2 className="mr-1.5 h-4 w-4" /> Cancelar</button>}
                  </div>
                </div>

                {isExpanded && installments.length > 0 && (
                  <div className="mt-5 rounded-xl border border-purple-100 bg-purple-50/40 p-3 sm:p-4">
                    <div className="mb-3 flex items-center justify-between">
                      <div><h4 className="font-semibold text-gray-900">Parcelas</h4><p className="text-sm text-gray-500">Acompanhe e registre os pagamentos individualmente.</p></div>
                      <span className="text-sm font-semibold text-purple-700">{installments.filter((installment) => installment.status === 'paid').length}/{installments.length} pagas</span>
                    </div>
                    <div className="space-y-2">
                      {installments.map((installment) => {
                        const [installmentLabel, installmentClass] = statusMap[installment.status] || [installment.status, 'bg-gray-100 text-gray-700 ring-gray-200'];
                        const installmentBalance = Math.max(Number(installment.amount || 0) - Number(installment.paid_amount || 0), 0);
                        const installmentProgress = Number(installment.amount || 0) > 0 ? Math.min((Number(installment.paid_amount || 0) / Number(installment.amount || 0)) * 100, 100) : 0;
                        const installmentDueBadge = getDueBadge(installment);
                        return (
                          <div key={installment.id} className="rounded-lg border border-gray-200 bg-white p-3">
                            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="font-semibold text-gray-900">{installment.installment_number}/{installment.total_installments}</span>
                                <span className="text-sm text-gray-600">Vence em {toDate(installment.due_date)}</span>
                                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ${installmentClass}`}>{installmentLabel}</span>
                                {installmentDueBadge && <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ${installmentDueBadge.className}`}>{installmentDueBadge.label}</span>}
                              </div>
                              <div className="flex flex-wrap items-center gap-3">
                                <span className="text-sm text-gray-600">Valor <strong className="text-gray-900">{formatCurrency(installment.amount)}</strong></span>
                                <span className="text-sm text-gray-600">Saldo <strong className="text-red-700">{formatCurrency(installmentBalance)}</strong></span>
                                {installmentBalance > 0 && installment.status !== 'cancelled' && <button onClick={() => openPayment(item, installment)} className="inline-flex items-center rounded-lg bg-green-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-green-700"><Wallet className="mr-1.5 h-3.5 w-3.5" /> Pagar</button>}
                              </div>
                            </div>
                            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-100"><div className="h-full rounded-full bg-green-500" style={{ width: `${installmentProgress}%` }} /></div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {filtered.length === 0 && (
            <div className="px-4 py-14 text-center">
              <CircleDollarSign className="mx-auto h-10 w-10 text-gray-300" />
              <h3 className="mt-3 font-semibold text-gray-900">Nenhuma despesa encontrada</h3>
              <p className="mt-1 text-sm text-gray-500">Ajuste os filtros ou cadastre uma nova despesa.</p>
            </div>
          )}
        </div>

        {visibleCount < filtered.length && (
          <div className="border-t border-gray-100 p-4 text-center">
            <button onClick={() => setVisibleCount((count) => count + 15)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">Mostrar mais</button>
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) setShowForm(false); }}>
          <div className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b p-5"><h3 className="text-lg font-semibold">{editing ? 'Editar despesa' : 'Nova despesa'}</h3><button onClick={() => setShowForm(false)}><X className="h-5 w-5" /></button></div>
            <form onSubmit={save} className="grid grid-cols-1 gap-4 p-5 md:grid-cols-2">
              <label className="md:col-span-2"><span className="mb-1 block text-sm font-medium">Descrição *</span><input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full rounded-lg border p-2" /></label>
              <label><span className="mb-1 block text-sm font-medium">Categoria *</span><select value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })} className="w-full rounded-lg border p-2"><option value="">Selecione</option>{categories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}</select></label>
              <label><span className="mb-1 block text-sm font-medium">Fornecedor</span><select value={form.supplier_id || ''} onChange={(e) => setForm({ ...form, supplier_id: e.target.value })} className="w-full rounded-lg border p-2"><option value="">Sem fornecedor</option>{suppliers.map((supplier) => <option key={supplier.id} value={supplier.id}>{supplier.name}</option>)}</select></label>

              {!editing && <div className="md:col-span-2 rounded-xl border border-purple-200 bg-purple-50 p-4"><p className="text-sm font-semibold text-purple-900">Forma de lançamento</p><div className="mt-3 flex flex-wrap gap-4"><label className="inline-flex items-center gap-2"><input type="radio" checked={form.payment_type === 'single'} onChange={() => setForm({ ...form, payment_type: 'single' })} /> À vista</label><label className="inline-flex items-center gap-2"><input type="radio" checked={form.payment_type === 'installment'} onChange={() => setForm({ ...form, payment_type: 'installment', paid_amount: '' })} /> Parcelada</label></div></div>}

              <label><span className="mb-1 block text-sm font-medium">Emissão</span><input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} className="w-full rounded-lg border p-2" /></label>
              {form.payment_type === 'installment' && !editing ? <><label><span className="mb-1 block text-sm font-medium">Primeiro vencimento *</span><input type="date" value={form.first_installment_date} onChange={(e) => setForm({ ...form, first_installment_date: e.target.value })} className="w-full rounded-lg border p-2" /></label><label><span className="mb-1 block text-sm font-medium">Quantidade de parcelas *</span><input type="number" min="2" max="120" value={form.installment_count} onChange={(e) => setForm({ ...form, installment_count: e.target.value })} className="w-full rounded-lg border p-2" /></label></> : <label><span className="mb-1 block text-sm font-medium">Vencimento *</span><input type="date" value={form.due_date} disabled={editing?.payment_type === 'installment'} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="w-full rounded-lg border p-2 disabled:bg-gray-100" /></label>}

              <label><span className="mb-1 block text-sm font-medium">Valor total *</span><CurrencyInput value={form.total_amount} disabled={editing?.payment_type === 'installment'} onValueChange={(value) => setForm({ ...form, total_amount: value })} /></label>
              {form.payment_type !== 'installment' && <label><span className="mb-1 block text-sm font-medium">Valor já pago</span><CurrencyInput value={form.paid_amount} onValueChange={(value) => setForm({ ...form, paid_amount: value })} /></label>}

              {installmentPreview && <div className="md:col-span-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">Serão geradas <strong>{installmentPreview.count} parcelas mensais</strong>. Valor aproximado por parcela: <strong>{formatCurrency(installmentPreview.first)}</strong>{installmentPreview.first !== installmentPreview.last && <>; a última será de <strong>{formatCurrency(installmentPreview.last)}</strong></>}.</div>}

              <label><span className="mb-1 block text-sm font-medium">Número do documento</span><input value={form.document_number || ''} onChange={(e) => setForm({ ...form, document_number: e.target.value })} className="w-full rounded-lg border p-2" /></label>
              <label><span className="mb-1 block text-sm font-medium">Competência</span><input type="month" value={form.competence || ''} onChange={(e) => setForm({ ...form, competence: e.target.value })} className="w-full rounded-lg border p-2" /></label>
              <label className="md:col-span-2"><span className="mb-1 block text-sm font-medium">Observações</span><textarea value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full rounded-lg border p-2" rows="3" /></label>
              <div className="md:col-span-2 flex justify-end gap-3 border-t pt-4"><button type="button" onClick={() => setShowForm(false)} className="rounded-lg border px-4 py-2">Cancelar</button><button disabled={saving} className="rounded-lg bg-purple-600 px-4 py-2 text-white disabled:opacity-60">{saving ? 'Salvando...' : 'Salvar despesa'}</button></div>
            </form>
          </div>
        </div>
      )}

      {paying && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
            <div className="flex justify-between"><div><h3 className="text-lg font-semibold">Registrar pagamento</h3><p className="text-sm text-gray-500">{paying.expense.description}{paying.installment ? ` · Parcela ${paying.installment.installment_number}/${paying.installment.total_installments}` : ''}</p></div><button onClick={() => setPaying(null)}><X className="h-5 w-5" /></button></div>
            <div className="mt-5 space-y-4">
              <label><span className="mb-1 block text-sm font-medium">Valor</span><CurrencyInput value={paymentAmount} onValueChange={setPaymentAmount} /></label>
              <label><span className="mb-1 block text-sm font-medium">Forma de pagamento</span><select value={paymentMethodId} onChange={(e) => setPaymentMethodId(e.target.value)} className="w-full rounded-lg border p-2"><option value="">Não informada</option>{paymentMethods.map((method) => <option key={method.id} value={method.id}>{method.name}</option>)}</select></label>
              <div className="flex justify-end gap-3"><button onClick={() => setPaying(null)} className="rounded-lg border px-4 py-2">Cancelar</button><button onClick={pay} className="rounded-lg bg-green-600 px-4 py-2 text-white">Confirmar pagamento</button></div>
            </div>
          </div>
        </div>
      )}

      {historyExpense && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) setHistoryExpense(null); }}>
          <div className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-xl bg-white shadow-xl">
            <div className="flex items-start justify-between border-b p-5"><div><h3 className="text-lg font-semibold">Histórico da despesa</h3><p className="mt-1 text-sm text-gray-500">{historyExpense.description}</p></div><button onClick={() => setHistoryExpense(null)}><X className="h-5 w-5" /></button></div>
            <div className="p-5">
              {historyLoading ? <div className="py-10 text-center text-sm text-gray-500">Carregando histórico...</div> : (
                <div className="relative ml-3 border-l-2 border-gray-200 pl-6">
                  <div className="relative pb-6"><span className="absolute -left-[31px] top-0 flex h-3 w-3 rounded-full bg-purple-600 ring-4 ring-purple-100" /><p className="text-sm font-semibold text-gray-900">Despesa cadastrada</p><p className="mt-1 text-sm text-gray-500">{toDate(historyExpense.created_at || historyExpense.issue_date)} · {formatCurrency(historyExpense.total_amount)}</p></div>
                  {historyPayments.map((payment) => (
                    <div key={payment.id} className="relative pb-6"><span className="absolute -left-[31px] top-0 flex h-3 w-3 rounded-full bg-green-600 ring-4 ring-green-100" /><p className="text-sm font-semibold text-gray-900">Pagamento registrado</p><p className="mt-1 text-sm text-gray-500">{toDate(payment.payment_date)} · {formatCurrency(payment.amount)}</p><p className="mt-1 text-xs text-gray-500">{payment.installment_number ? `Parcela ${payment.installment_number}` : 'Despesa principal'}{payment.payment_method_name ? ` · ${payment.payment_method_name}` : ''}</p>{payment.notes && <p className="mt-2 rounded-lg bg-gray-50 p-2 text-sm text-gray-600">{payment.notes}</p>}</div>
                  ))}
                  {historyPayments.length === 0 && <div className="relative"><span className="absolute -left-[31px] top-0 flex h-3 w-3 rounded-full bg-gray-400 ring-4 ring-gray-100" /><p className="text-sm text-gray-500">Nenhum pagamento registrado até o momento.</p></div>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpenseManagementPanel;
